import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, KV, Stat, StatGrid, CodeBlock, Chip } from '../components/Section';
import { ModuleRemediation } from '../components/Remediation';
import type { EmailModule } from '../types';

type Tone = 'ok' | 'warn' | 'bad';
const TONE_ACCENT: Record<Tone, 'green' | 'amber' | 'rose'> = { ok: 'green', warn: 'amber', bad: 'rose' };

interface Assessment {
  tone: Tone;
  label: string;
  banner: { tone: Tone; title: string; body: string };
}

// SPF strength is the qualifier, not mere presence: -all enforces, ~all/?all
// only signal, +all authorizes everyone.
function assessSpf(spf: EmailModule['spf']): Assessment {
  if (!spf.configured) {
    return { tone: 'bad', label: 'MISSING',
      banner: { tone: 'bad', title: 'SPF record not found', body: 'Anyone can send mail claiming to be from your domain.' } };
  }
  switch (spf.qualifier) {
    case 'hard_fail':
      return { tone: 'ok', label: 'STRICT (-all)',
        banner: { tone: 'ok', title: 'SPF enforces a strict policy (-all)', body: 'Mail from unlisted senders is rejected — the strongest SPF stance.' } };
    case 'soft_fail':
      return { tone: 'warn', label: 'SOFTFAIL (~all)',
        banner: { tone: 'warn', title: 'SPF uses softfail (~all)', body: 'Mail from unlisted senders is accepted but marked. Move to -all once every legitimate sender is confirmed.' } };
    case 'neutral':
      return { tone: 'warn', label: 'NEUTRAL (?all)',
        banner: { tone: 'warn', title: 'SPF policy is neutral (?all)', body: 'It lists senders but enforces nothing — recipients get no signal to act on. Move to ~all or -all.' } };
    case 'pass':
      return { tone: 'bad', label: 'PASS (+all)',
        banner: { tone: 'bad', title: 'SPF uses +all — this authorizes any sender', body: 'A +all policy lets the entire internet pass SPF for your domain. Change it to -all (or ~all) immediately.' } };
    default:
      return { tone: 'warn', label: 'NO ALL MECHANISM',
        banner: { tone: 'warn', title: 'SPF record has no trailing `all` mechanism', body: 'Without -all/~all the policy is undefined at the boundary. Add ~all or -all.' } };
  }
}

// DMARC strength is the policy: reject blocks, quarantine spam-files, none only
// monitors.
function assessDmarc(d: EmailModule['dmarc']): Assessment {
  if (!d.configured) {
    return { tone: 'bad', label: 'MISSING',
      banner: { tone: 'bad', title: 'DMARC record not found', body: "Without DMARC, recipients can't reliably reject spoofed mail from your domain." } };
  }
  switch (d.policy) {
    case 'reject':
      return { tone: 'ok', label: 'REJECT',
        banner: { tone: 'ok', title: 'DMARC is enforcing (p=reject)', body: 'Mail that fails authentication is rejected — full anti-spoofing protection.' } };
    case 'quarantine':
      return { tone: 'warn', label: 'QUARANTINE',
        banner: { tone: 'warn', title: 'DMARC is partially enforcing (p=quarantine)', body: 'Failing mail is sent to spam rather than rejected. Move to p=reject for full protection.' } };
    default:
      return { tone: 'bad', label: 'NONE (monitor-only)',
        banner: { tone: 'bad', title: 'DMARC is monitor-only (p=none)', body: 'It collects reports but does NOT block spoofed mail. Progress to p=quarantine, then p=reject.' } };
  }
}

export function EmailPage() {
  const scan = useScan();
  const m = scan.modules.email;

  if (!m) return (
    <>
      <PageHeader title="Email Security" subtitle="Module result not available." />
      <Section accent="amber"><Banner tone="warn" title="Email analyzer didn't return a result" /></Section>
    </>
  );

  const qual = m.spf.qualifier?.toUpperCase().replace('_', ' ') ?? 'NOT SET';
  const dmarcPolicy = (m.dmarc.policy ?? 'NONE').toUpperCase();
  const spfA = assessSpf(m.spf);
  const dmarcA = assessDmarc(m.dmarc);
  const dmarcPct = m.dmarc.pct ?? 100;
  const weakDkim = m.dkim.selectors.some(s => s.key_size != null && s.key_size < 2048);
  // A selector exists AND can actually sign (not revoked, real parsed key).
  // `configured` alone is true even for a revoked-only selector, which must not
  // read as a healthy DKIM setup.
  const activeDkim = m.dkim.selectors.some(s => !s.revoked && (s.key_size ?? 0) > 0);

  return (
    <>
      <PageHeader
        title="Email Security Analysis"
        subtitle="SPF, DKIM, DMARC, BIMI, MTA-STS, and TLS-RPT email authentication checks"
      />

      <Section title="Email Security Overview" accent="blue">
        <StatGrid cols={6}>
          <Stat label="SPF" value={qual}
                tone={spfA.tone} sub="Policy qualifier" />
          <Stat label="DMARC" value={dmarcPolicy}
                tone={dmarcA.tone}
                sub="Domain alignment policy" />
          <Stat label="DKIM" value={!m.dkim.configured ? 'NONE' : !activeDkim ? 'REVOKED' : weakDkim ? 'WEAK KEY' : 'FOUND'}
                tone={!m.dkim.configured ? 'warn' : !activeDkim ? 'bad' : weakDkim ? 'warn' : 'ok'} sub="Cryptographic signing" />
          <Stat label="Grade" value={m.grade}
                tone={m.grade === 'A' ? 'ok' : m.grade === 'B' ? 'info' :
                      m.grade === 'C' ? 'warn' : 'bad'} sub="Channel risk grade" />
          <Stat label="BIMI" value={m.bimi.configured ? 'SET' : 'OFF'}
                tone={m.bimi.configured ? 'ok' : 'mute'} sub="Brand indicators" />
          <Stat label="MTA-STS" value={m.mta_sts.configured ? (m.mta_sts.mode ?? 'SET').toUpperCase() : 'OFF'}
                tone={!m.mta_sts.configured ? 'mute'
                    : m.mta_sts.mode === 'enforce' ? 'ok'
                    : m.mta_sts.mode === 'testing' || m.mta_sts.mode === 'none' ? 'warn' : 'info'}
                sub="TLS delivery policy" />
        </StatGrid>
      </Section>

      {/* Microsoft 365 tenant cloud — authoritative, from the federation endpoint */}
      {m.microsoft?.is_microsoft && (
        <Section
          title="Microsoft 365 Tenant"
          accent="blue"
          status={{ label: m.microsoft.label.toUpperCase(), tone: m.microsoft.sovereign ? 'info' : 'mute' }}
        >
          <Banner
            tone="info"
            title={m.microsoft.sovereign
              ? `Sovereign Microsoft cloud: ${m.microsoft.label}`
              : `Microsoft 365 ${m.microsoft.label} tenant`}
          >
            {m.microsoft.sovereign
              ? 'Resolved authoritatively from the Microsoft federation endpoint (not inferred from MX, which cannot distinguish GCC High from DoD). Sovereign-cloud residency is directly relevant to CMMC, ITAR, and DoD compliance, and confirms which tenant a domain migration has actually landed in.'
              : 'This domain authenticates to the Microsoft 365 commercial (Global) cloud, per the Microsoft federation endpoint.'}
          </Banner>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <KV k="Tenant cloud" v={m.microsoft.label} />
            <KV k="Federation provider" v={m.microsoft.provider} />
            <KV k="Sovereign cloud" v={m.microsoft.sovereign ? 'Yes' : 'No'} />
          </div>
        </Section>
      )}

      {/* SPF */}
      <Section title="SPF (Sender Policy Framework)"
        accent={TONE_ACCENT[spfA.tone]}
        status={{ label: spfA.label, tone: spfA.tone }}>
        {m.spf.configured ? (
          <>
            <Banner tone={spfA.banner.tone} title={spfA.banner.title}>
              {spfA.banner.body}
            </Banner>
            <div className="mt-4">
              <CodeBlock label="SPF Record">{m.spf.record ?? ''}</CodeBlock>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-4">
              <KV k="Authorized Senders" v={m.spf.authorized_senders.join(', ') || '—'} />
              <KV k="Policy Action" v={qual} />
              <KV k="RFC Compliance" v={m.spf.rfc7208_compliant
                ? <span className="text-emerald-600">RFC 7208 Compliant</span>
                : <span className="text-rose-600">Non-compliant</span>} />
              <KV k="DNS Lookups" v={m.spf.dns_lookups != null
                ? <span className={m.spf.evaluation === 'permerror' ? 'text-rose-600' : m.spf.dns_lookups > 8 ? 'text-amber-600' : 'text-ink'}>
                    {m.spf.dns_lookups} / 10{m.spf.evaluation === 'permerror' ? ' — PermError' : ''}
                  </span>
                : '—'} />
              {m.spf.void_lookups != null && (
                <KV k="Void Lookups" v={
                  <span className={m.spf.void_lookups > 2 ? 'text-rose-600' : m.spf.void_lookups > 0 ? 'text-amber-600' : 'text-ink'}>
                    {m.spf.void_lookups} / 2
                  </span>
                } />
              )}
            </div>
            {m.spf.evaluation === 'permerror' && (
              <div className="mt-3">
                <Banner tone="bad" title="SPF exceeds the RFC 7208 lookup limit">
                  {m.spf.permerror_reason ?? 'Too many DNS lookups.'} Receivers may treat SPF as PermError and ignore it — collapse includes or switch to IP ranges.
                </Banner>
              </div>
            )}
          </>
        ) : (
          <Banner tone="bad" title="SPF record not found">
            Anyone can send mail claiming to be from your domain.
          </Banner>
        )}
      </Section>

      {/* DMARC */}
      <Section title="DMARC (Domain-based Message Authentication)"
        accent={TONE_ACCENT[dmarcA.tone]}
        status={{ label: dmarcA.label, tone: dmarcA.tone }}>
        {m.dmarc.configured ? (
          <>
            <Banner tone={dmarcA.banner.tone} title={dmarcA.banner.title}>
              {dmarcA.banner.body}
            </Banner>
            <div className="mt-4">
              <CodeBlock label="DMARC Record">{m.dmarc.record ?? ''}</CodeBlock>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <KV k="Policy Action" v={
                <span className={
                  m.dmarc.policy === 'reject' ? 'text-emerald-600' :
                  m.dmarc.policy === 'quarantine' ? 'text-amber-600' : 'text-rose-600'
                }>{dmarcPolicy}</span>
              } />
              <KV k="Enforcement %" v={
                <span className={dmarcPct < 100 ? 'text-amber-600' : ''}>
                  {dmarcPct}%{dmarcPct < 100 && ' (partial)'}
                </span>
              } />
              <KV k="Alignment" v={`SPF: ${(m.dmarc.alignment_spf ?? 'r').toUpperCase()} | DKIM: ${(m.dmarc.alignment_dkim ?? 'r').toUpperCase()}`} />
              <KV k="Aggregate Reports (rua)" v={m.dmarc.rua?.length ? m.dmarc.rua.join(', ') : '—'} />
              <KV k="Forensic Reports (ruf)" v={m.dmarc.ruf?.length ? m.dmarc.ruf.join(', ') : '—'} />
            </div>
            {m.dmarc.platform && (
              <div className="mt-4 tint-ok rounded-lg border p-4">
                <p className="text-sm font-semibold text-emerald-900">DMARC Management Platform</p>
                <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                  <div><b>Platform:</b> {m.dmarc.platform.name}</div>
                  <div><b>Confidence:</b> {m.dmarc.platform.confidence}%</div>
                  <div className="sm:col-span-2"><b>Description:</b> {m.dmarc.platform.description}</div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Banner tone="bad" title="DMARC record not found">
            Without DMARC, recipients can't reliably reject spoofed mail from your domain.
          </Banner>
        )}
      </Section>

      {/* DKIM */}
      <Section title="DKIM (DomainKeys Identified Mail)"
        accent={activeDkim ? 'green' : 'amber'}
        status={{ label: !m.dkim.configured ? 'NONE FOUND' : activeDkim ? 'CONFIGURED' : 'REVOKED',
                  tone: activeDkim ? 'ok' : 'warn' }}>
        {m.dkim.configured ? (
          <>
            {activeDkim ? (
              <Banner tone="ok" title="DKIM signatures are configured and active">
                Your domain uses DKIM cryptographic signatures to verify email integrity
                and authenticity.
              </Banner>
            ) : (
              <Banner tone="warn" title="DKIM selector found but not active">
                The DKIM selector(s) we found are revoked (empty public key) or unparseable,
                so they can't sign mail. Publish a valid key to enable DKIM.
              </Banner>
            )}
            <div className="mt-4 space-y-4">
              {m.dkim.selectors.map((s, i) => (
                <div key={i} className="rounded-lg border border-line bg-panel-2/60 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">selector: <span className="font-mono">{s.selector}</span></p>
                    {s.revoked
                      ? <Chip tone="bad">Revoked</Chip>
                      : s.testing
                        ? <Chip tone="warn">Testing</Chip>
                        : <Chip tone="ok">Production</Chip>}
                  </div>
                  <div className="mt-2">
                    <CodeBlock>{s.record}</CodeBlock>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <KV k="Algorithm" v={s.algorithm ?? 'RSA'} />
                    <KV k="Key Size" v={
                      s.revoked ? <span className="text-rose-600">revoked</span> :
                      s.key_size
                        ? <span className={s.key_size < 2048 ? 'text-amber-600' : ''}>
                            {s.key_size < 2048 ? `${s.key_size}-bit (weak)` : `${s.key_size}+ bits`}
                          </span>
                        : '—'
                    } />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <Banner tone="warn" title="No common DKIM selectors found">
            We probed common selectors. Add the actual selector to your DNS or
            inspect a recent message header for the `s=` tag.
          </Banner>
        )}
      </Section>

      {/* RFC compliance + auth score */}
      <Section title="RFC Compliance & Authentication Score" accent="blue">
        <div className="grid gap-5 lg:grid-cols-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">RFC Compliance Summary</p>
            <ul className="mt-2 space-y-1.5 text-sm">
              <ComplianceRow label="SPF (RFC 7208)" ok={m.spf.rfc7208_compliant} />
              <ComplianceRow label="DMARC (RFC 7489)" ok={m.dmarc.configured} />
              <ComplianceRow label="DKIM (RFC 6376)" ok={activeDkim} okLabel="IMPLEMENTED" />
            </ul>
          </div>
          <div className="rounded-lg border border-line bg-panel-2/60 p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">Authentication Score</p>
            <p className={`tnum mt-1 text-5xl font-bold ${
              m.authentication_score >= 90 ? 'text-emerald-600' :
              m.authentication_score >= 70 ? 'text-sky-600' :
              m.authentication_score >= 40 ? 'text-amber-600' : 'text-rose-600'
            }`}>{m.authentication_score}%</p>
            <p className="mt-1 text-xs text-ink-3">Email Authentication Strength</p>
            <div className="mx-auto mt-3 h-2 max-w-xs rounded-full bg-slate-200">
              <div className={`h-2 rounded-full ${
                m.authentication_score >= 90 ? 'bg-linear-to-r from-emerald-400 to-emerald-600' :
                m.authentication_score >= 70 ? 'bg-linear-to-r from-sky-400 to-sky-600' :
                m.authentication_score >= 40 ? 'bg-linear-to-r from-amber-400 to-amber-600' :
                                              'bg-linear-to-r from-rose-400 to-rose-600'
              }`} style={{ width: `${m.authentication_score}%` }} />
            </div>
            <p className="mt-3 text-xs text-ink-2">
              {m.authentication_score >= 90 && 'Excellent: complete email auth stack.'}
              {m.authentication_score >= 70 && m.authentication_score < 90 && 'Good: most mechanisms in place.'}
              {m.authentication_score < 70 && 'Improvement needed: configure missing mechanisms.'}
            </p>
          </div>
        </div>
      </Section>

      {/* Advanced features */}
      <Section title="Advanced Email Security Features" accent="blue">
        <div className="grid gap-3 md:grid-cols-3">
          <FeatureCard title="BIMI" set={m.bimi.configured}
            description="Verified brands display logos next to authenticated emails."
            requirements="DMARC enforcement, SVG logo, VMC certificate"
            detail={[
              m.bimi.logo_url && `logo: ${m.bimi.logo_url}`,
              m.bimi.vmc_url && `VMC: ${m.bimi.vmc_url}`
            ].filter(Boolean).join(' · ') || (m.bimi.configured ? 'BIMI record published' : undefined)} />
          <FeatureCard title="MTA-STS" set={m.mta_sts.configured}
            statusOverride={
              !m.mta_sts.configured ? { label: 'NOT SET', tone: 'warn' }
              : m.mta_sts.mode === 'enforce' ? { label: 'ENFORCE', tone: 'ok' }
              : m.mta_sts.mode === 'testing' ? { label: 'TESTING (no enforcement)', tone: 'warn' }
              : m.mta_sts.mode === 'none' ? { label: 'MODE: NONE', tone: 'warn' }
              : { label: 'SET', tone: 'info' }
            }
            description="Enforces TLS encryption for inbound mail to prevent downgrade attacks. Only mode=enforce actually blocks downgrades; testing mode just reports."
            requirements="HTTPS-served policy file, MX patterns, mode=enforce"
            warnings={m.mta_sts.issues}
            detail={m.mta_sts.configured
              ? [
                  m.mta_sts.mode && `mode=${m.mta_sts.mode}`,
                  m.mta_sts.max_age && `max_age=${m.mta_sts.max_age}`,
                  m.mta_sts.mx_covered != null && (m.mta_sts.mx_covered ? 'MX: all covered' : 'MX: gaps'),
                  m.mta_sts.mx_patterns?.length && `mx: ${m.mta_sts.mx_patterns.join(', ')}`
                ].filter(Boolean).join(' · ') || 'policy published'
              : undefined} />
          <FeatureCard title="TLS-RPT" set={m.tls_rpt.configured} optional
            description="Reports on TLS connection failures during email delivery."
            requirements="Reporting endpoint configured"
            detail={m.tls_rpt.rua?.length ? m.tls_rpt.rua.join(', ') : (m.tls_rpt.configured ? 'reporting endpoint set' : undefined)} />
        </div>
      </Section>

      {scan.remediations.some(r => r.module === 'email') && (
        <Section title="How to fix" accent="blue">
          <ModuleRemediation remediations={scan.remediations} modules={['email']} />
        </Section>
      )}
    </>
  );
}

function ComplianceRow({ label, ok, okLabel = 'COMPLIANT' }: { label: string; ok: boolean; okLabel?: string }) {
  return (
    <li className="flex items-center justify-between rounded-md bg-panel-2/60 px-3 py-2">
      <span className="text-ink-2">{label}</span>
      <span className={`pill ${ok ? 'pill-ok' : 'pill-warn'}`}>{ok ? okLabel : 'MISSING'}</span>
    </li>
  );
}

function FeatureCard({ title, set, description, requirements, optional, statusOverride, detail, warnings }: {
  title: string; set: boolean; description: string; requirements: string; optional?: boolean;
  statusOverride?: { label: string; tone: 'ok' | 'warn' | 'info' | 'mute' };
  detail?: React.ReactNode;
  warnings?: string[];
}) {
  // `set` always wins: a configured feature reads CONFIGURED even when it's
  // optional. `optional` only softens the NOT-configured state (info "OPTIONAL"
  // instead of a warning "NOT SET"), so optional features aren't flagged as gaps.
  const tone = statusOverride?.tone ?? (set ? 'ok' : optional ? 'info' : 'warn');
  const status = statusOverride?.label ?? (set ? 'CONFIGURED' : optional ? 'OPTIONAL' : 'NOT SET');
  const borderColor = tone === 'ok' ? 'border-l-emerald-500'
                    : tone === 'info' ? 'border-l-sky-500'
                    : tone === 'mute' ? 'border-l-slate-300' : 'border-l-amber-500';
  const pillCls = tone === 'ok' ? 'pill-ok' : tone === 'info' ? 'pill-info'
                : tone === 'mute' ? 'pill-mute' : 'pill-warn';
  return (
    <div className={`rounded-lg border border-line border-l-4 bg-panel p-4 ${borderColor}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold">{title}</h3>
        <span className={`pill ${pillCls}`}>
          {status}
        </span>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-ink-2">{description}</p>
      {set && warnings && warnings.length > 0 && (
        <ul className="mt-2 space-y-1 text-[11px] text-amber-700">
          {warnings.map((w, i) => <li key={i}>• {w}</li>)}
        </ul>
      )}
      {set && detail ? (
        <p className="mt-3 break-all text-[11px] text-ink-2">
          <b className="text-ink-2">Configured:</b> {detail}
        </p>
      ) : (
        <p className="mt-3 text-[11px] text-ink-3">
          <b className="text-ink-2">Requirements:</b> {requirements}
        </p>
      )}
    </div>
  );
}

